import omsOrdersColumnConfig from '../omsOrdersColumnConfig'
import {
  DashboardPendingOrderAction,
  isDashboardPendingOrderAction,
} from '../../dashboard/utils/pendingActionNavigation'

const SOURCE_ORDER_CHANNEL_TYPES = new Set(['primary', 'accounting', 'shipping', 'erp', 'oms'])

const PLATFORM_CHANNEL_TYPE_BY_FLAG: Array<{flag: string; channelType: string}> = [
  {flag: 'isAccountingStore', channelType: 'accounting'},
  {flag: 'isShippingStore', channelType: 'shipping'},
  {flag: 'isErpStore', channelType: 'erp'},
  {flag: 'isOmsStore', channelType: 'oms'},
  {flag: 'isMainStore', channelType: 'primary'},
]

export const resolveSourceStatusKey = ({
  fields = [],
  platformIdHint = null,
  connectedPlatforms = [],
}: {
  fields?: any[]
  platformIdHint?: string | null
  connectedPlatforms?: any[]
}) => {
  const numericPlatformId = Number(platformIdHint)
  const hasValidPlatformHint = Number.isFinite(numericPlatformId)

  let sourceChannelTypeHint: string | null = null
  if (hasValidPlatformHint && connectedPlatforms?.length) {
    const selectedPlatform = connectedPlatforms.find(
      (item: any) => Number(item?.id) === numericPlatformId
    )
    if (selectedPlatform) {
      for (const mapping of PLATFORM_CHANNEL_TYPE_BY_FLAG) {
        if (selectedPlatform?.[mapping.flag]) {
          sourceChannelTypeHint = mapping.channelType
          break
        }
      }
    }
  }

  let firstKey: string | null = null
  let primaryKey: string | null = null
  let syncStatusKey: string | null = null
  const byChannelType = new Map<string, string>()
  const byPlatformId = new Map<number, string>()

  for (const field of fields || []) {
    const key = field?.column_search
    const channelType = field?.channel_type
    if (!key || !SOURCE_ORDER_CHANNEL_TYPES.has(channelType)) continue

    if (!firstKey) firstKey = key
    if (channelType === 'primary' && !primaryKey) primaryKey = key
    if (key === 'sync_status' && !syncStatusKey) syncStatusKey = key

    if (!byChannelType.has(channelType)) {
      byChannelType.set(channelType, key)
    }

    const fieldPlatformId = Number(field?.platform_id)
    if (Number.isFinite(fieldPlatformId) && !byPlatformId.has(fieldPlatformId)) {
      byPlatformId.set(fieldPlatformId, key)
    }
  }

  if (sourceChannelTypeHint && byChannelType.has(sourceChannelTypeHint)) {
    return byChannelType.get(sourceChannelTypeHint) || 'sync_status'
  }

  if (hasValidPlatformHint && byPlatformId.has(numericPlatformId)) {
    return byPlatformId.get(numericPlatformId) || 'sync_status'
  }

  return primaryKey || syncStatusKey || firstKey || 'sync_status'
}

export const resolveShipmentStatusKey = (fields: any[] = []) => {
  const shipmentField = fields.find((field) => field?.channel_type === 'shipment')
  return shipmentField?.column_search ?? 'shipment_status'
}

/** Supports pending_action and legacy sync_status / shipment_status query params. */
export const parseDashboardPendingOrderActionFromSearchParams = (
  searchParams: URLSearchParams
): DashboardPendingOrderAction | null => {
  const pendingAction = searchParams.get('pending_action')
  if (isDashboardPendingOrderAction(pendingAction)) {
    return pendingAction
  }

  const syncStatus = (searchParams.get('sync_status') || '').trim().toUpperCase()
  if (syncStatus === 'ERROR') return 'failed_order_sync'
  if (syncStatus === 'PENDING') return 'pending_orders'

  const shipmentStatus = (searchParams.get('shipment_status') || '').trim().toUpperCase()
  if (shipmentStatus === 'PENDING') return 'to_be_shipped'

  return null
}

export const resolveDashboardPendingActionFilters = (
  action: DashboardPendingOrderAction,
  opts: {
    metadataFields: any[]
    connectedPlatforms: any[]
    isOmsView: boolean
  }
): Record<string, string> | null => {
  const {metadataFields, isOmsView} = opts
  const orderStatusSearchKey = omsOrdersColumnConfig.orderStatusColumn.searchFilterKey

  switch (action) {
    case 'failed_order_sync':
      if (isOmsView) {
        return {[orderStatusSearchKey]: 'ERROR'}
      }
      {
        const key = resolveSourceStatusKey({
          fields: metadataFields,
          platformIdHint: null,
          connectedPlatforms: opts.connectedPlatforms,
        })
        return key ? {[key]: 'ERROR'} : null
      }
    case 'pending_orders':
      if (isOmsView) {
        return {[orderStatusSearchKey]: 'PENDING'}
      }
      {
        const key = resolveSourceStatusKey({
          fields: metadataFields,
          platformIdHint: null,
          connectedPlatforms: opts.connectedPlatforms,
        })
        return key ? {[key]: 'PENDING'} : null
      }
    case 'to_be_shipped': {
      const key = isOmsView
        ? omsOrdersColumnConfig.replacedDynamicStatusColumns.shipment.filterKey
        : resolveShipmentStatusKey(metadataFields)
      return {[key]: 'PENDING'}
    }
    default:
      return null
  }
}

/** Reset all known order column filter keys, then apply dashboard deep-link values only. */
export const buildDashboardDeepLinkColumnFilters = (
  allFilterKeys: string[],
  deepLinkFilters: Record<string, string>
): Record<string, string> => {
  const cleared = allFilterKeys.reduce<Record<string, string>>((acc, key) => {
    if (key) acc[key] = ''
    return acc
  }, {})
  return {...cleared, ...deepLinkFilters}
}
